/**
 * Os erros da fórmula — cada um com nome próprio.
 *
 * A T2.4 do card pede que divisão por zero, coluna apagada, tipo incompatível e sintaxe
 * inválida tenham **mensagem própria na célula**. A razão está escrita na regra 8.9 do
 * `CLAUDE.md`: *default que finge* custa caro em qualquer lugar, e numa fórmula financeira
 * custa mais — um `0` no lugar de um erro é um número que alguém soma.
 *
 * ⚠️ Por isso **nada aqui devolve valor de reserva**. Um erro é um erro; quem o exibe decide
 * como mostrá-lo, e a célula mostra o texto. O que não acontece nunca é a fórmula responder
 * um número que ninguém calculou.
 */

/** O que deu errado. O código é estável (entra em teste); a mensagem é para gente. */
export type CodigoDeErro =
  | 'sintaxe'          // a expressão não é uma expressão
  | 'coluna-ausente'   // prop("X") aponta para coluna que não existe (ou foi apagada)
  | 'ciclo'            // A depende de B que depende de A
  | 'tipo'             // somar texto com data, comparar número com tag…
  | 'divisao-por-zero'
  | 'funcao-ausente'   // nome que não é função nossa
  | 'argumentos'       // função certa, quantidade ou tipo de argumento errado
  | 'profundidade';    // expressão aninhada além do limite — proteção de pilha

export class ErroDeFormula extends Error {
  readonly codigo: CodigoDeErro;
  /** Posição no texto da expressão, quando se sabe. O editor sublinha a partir dela. */
  readonly posicao: number | null;

  constructor(codigo: CodigoDeErro, mensagem: string, posicao: number | null = null) {
    super(mensagem);
    this.name = 'ErroDeFormula';
    this.codigo = codigo;
    this.posicao = posicao;
  }
}

export const erro = (
  codigo: CodigoDeErro,
  mensagem: string,
  posicao: number | null = null
): ErroDeFormula => new ErroDeFormula(codigo, mensagem, posicao);

/**
 * O texto curto que aparece DENTRO da célula.
 *
 * ⚠️ Curto de propósito: a célula de uma tabela financeira tem a largura de um número, e uma
 * frase longa é truncada pelo CSS — o que produz exatamente o defeito que esta função existe
 * para evitar (o usuário vê `#Erro: não foi possí…` e não sabe o quê). A frase inteira vai
 * para o `title`/tooltip, que é onde cabe.
 */
export function rotuloCurto(e: ErroDeFormula): string {
  switch (e.codigo) {
    case 'sintaxe': return '#Sintaxe';
    case 'coluna-ausente': return '#Coluna?';
    case 'ciclo': return '#Ciclo';
    case 'tipo': return '#Tipo';
    case 'divisao-por-zero': return '#÷0';
    case 'funcao-ausente': return '#Função?';
    case 'argumentos': return '#Args';
    case 'profundidade': return '#Fundo';
    default: return '#Erro';
  }
}
